import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { logout } from "../redux/actions/authActions";
import { shouldRefreshToken, isRefreshTokenValid } from "../utils/tokenUtils";
import { jwtRefreshToken } from "../services/jwtAuthService";

/**
 * Custom hook to handle automatic token refresh
 * Checks every 60 seconds if token needs refresh
 * Refreshes token if it expires within 5 minutes
 * Logs out user if refresh token is expired
 */
export const useTokenRefresh = () => {
    const dispatch = useDispatch();
    const intervalRef = useRef(null);
    const isRefreshingRef = useRef(false);
    const hasLoggedOutRef = useRef(false);

    useEffect(() => {
        const checkAndRefreshToken = async () => {
            // Prevent multiple simultaneous refresh attempts
            if (isRefreshingRef.current || hasLoggedOutRef.current) {
                return;
            }

            try {
                // Check if refresh token is valid first
                const isRefreshValid = await isRefreshTokenValid();

                if (!isRefreshValid) {
                    if (!hasLoggedOutRef.current) {
                        console.warn("Refresh token is expired. Logging out...");
                        hasLoggedOutRef.current = true;
                        dispatch(logout());
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                        }
                    }
                    return;
                }

                // Check if access token needs refresh (expires within 5 min)
                const needsRefresh = await shouldRefreshToken();

                if (needsRefresh) {
                    console.log("Access token expiring soon. Refreshing...");
                    isRefreshingRef.current = true;

                    try {
                        await jwtRefreshToken();
                        console.log("Token refreshed successfully");
                    } catch (error) {
                        if (error.message === "REFRESH_TOKEN_EXPIRED") {
                            console.error("Refresh token expired during refresh attempt");
                            if (!hasLoggedOutRef.current) {
                                hasLoggedOutRef.current = true;
                                dispatch(logout());
                                if (intervalRef.current) {
                                    clearInterval(intervalRef.current);
                                }
                            }
                        } else {
                            console.error("Failed to refresh token:", error);
                        }
                    } finally {
                        isRefreshingRef.current = false;
                    }
                }
            } catch (error) {
                console.error("Error in token refresh check:", error);
                isRefreshingRef.current = false;
            }
        };

        // Check immediately on mount
        checkAndRefreshToken();

        // Then check every 60 seconds
        intervalRef.current = setInterval(checkAndRefreshToken, 60000);

        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [dispatch]);
};

export default useTokenRefresh;
